import { useId } from "react";

/**
 * Nastro di testo scorrevole (ticker), guidato dai dati.
 *
 * Prima i testi erano scritti dentro la homepage, tre voci fisse in un ordine
 * fisso: aggiungerne una, toglierla o cambiarne l'ordine significava modificare
 * il componente. Qui le voci arrivano da fuori, così il contenuto vive nei file
 * di traduzione e il componente si limita a mostrarlo.
 *
 * Accessibilità: la ripetizione serve solo a riempire lo schermo, quindi le
 * copie visive sono nascoste agli screen reader e l'elenco viene esposto una
 * volta sola come testo leggibile. L'animazione si ferma se l'utente ha chiesto
 * meno animazioni dal sistema operativo (WCAG 2.2.2: un movimento infinito che
 * non si può fermare è una barriera).
 */

export interface TextMarqueeProps {
  /** Voci da mostrare, nell'ordine in cui devono scorrere. */
  items: string[];
  /** Testo alternativo per gli screen reader (descrive l'insieme). */
  ariaLabel: string;
  /** Separatore tra una voce e l'altra. */
  separator?: string;
  /** Durata di un giro completo. Più alta = più lento. */
  durationSeconds?: number;
  background?: string;
  color?: string;
}

/**
 * Numero di copie del blocco di voci. DEVE restare pari: l'animazione trasla di
 * -50%, quindi la seconda metà deve essere identica alla prima perché il ciclo
 * si chiuda senza scatti. Con un numero dispari si vedrebbe un salto a ogni giro.
 */
const REPEATS = 10;

export function TextMarquee({
  items,
  ariaLabel,
  separator = "•",
  durationSeconds = 22,
  background,
  color = "#fff",
}: TextMarqueeProps) {
  const animationName = `gb-marquee-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;

  // Nessuna voce: non rendo una fascia vuota, che sarebbe solo una banda colorata.
  const visible = items.filter((s) => typeof s === "string" && s.trim().length > 0);
  if (visible.length === 0) return null;

  const line = visible.join(` ${separator} `);

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      style={{
        background,
        borderTop: "1px solid rgba(255,255,255,0.05)",
        padding: "14px 0",
        overflow: "hidden",
        whiteSpace: "nowrap",
      }}
    >
      {/* Elenco leggibile una sola volta dagli screen reader, senza le ripetizioni. */}
      <span
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {line}
      </span>

      <div aria-hidden="true" style={{ display: "inline-flex", animation: `${animationName} ${durationSeconds}s linear infinite` }}>
        {Array.from({ length: REPEATS }).map((_, i) => (
          <span
            key={i}
            style={{
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: 4,
              textTransform: "uppercase" as const,
              color,
              paddingRight: 48,
            }}
          >
            {line} {separator}
          </span>
        ))}
      </div>

      <style>{`
        @keyframes ${animationName} {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="${animationName}"] { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
